import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import AuthShell from "@/components/AuthShell";
import LanguageSelector from "@/components/LanguageSelector";
import { ArrowRight, Building2, KeyRound, Mail, Phone, UserRound, Loader2 } from "lucide-react";


import { API_BASE_URL as API } from "../apiConfig";
export default function RegisterPage() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordMismatch') || "两次输入的密码不一致");
      return;
    }

    if (formData.password.length < 6) {
      setError(t('passwordTooShort') || "密码长度至少为6个字符");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/register`, {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      let errorMsg = t('registerFailed') || "注册失败，请稍后重试";
      
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.request && !err.response) {
        errorMsg = "网络错误：服务器无响应，请检查网络连接";
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-success-shell">
        <div className="auth-card max-w-md text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-success/15 text-success">
            <ArrowRight className="h-7 w-7" />
          </div>
          <h2 className="premium-panel-title mb-2 text-2xl">
            {t('registerSuccess')}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t('registerSuccessMsg')}
          </p>
          <p className="text-sm text-muted-foreground">{t('redirecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <AuthShell
      eyebrow="SHONAN INTERNATIONAL"
      title={t('registerTitle')}
      subtitle={t('registerSubtitle')}
      languageSelector={<LanguageSelector variant="large" />}
    >
      <div className="auth-card">
        <div className="mb-8">
          <p className="auth-form-kicker">{t('register')}</p>
          <h2 className="auth-form-title">{t('registerSubtitle')}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t('registerSuccessMsg')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">
              {t('companyName')} *
            </label>
            <div className="relative">
              <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="premium-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">
              {t('email')} *
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="premium-input pl-10"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">
              {t('phoneOptional')}
            </label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="premium-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">
              {t('password')} *
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="premium-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground/80">
              {t('confirmPassword')} *
            </label>
            <div className="relative">
              <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="premium-input pl-10"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="premium-button inline-flex w-full items-center justify-center gap-2 mt-6 bg-primary text-primary-foreground shadow-[0_18px_36px_-18px_hsl(var(--primary)/0.75)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            <span className="ml-2">{loading ? t('loading') : t('register')}</span>
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('hasAccount')}
            <Link
              to="/login"
              className="ml-1 font-semibold text-primary hover:text-primary/80"
            >
              {t('loginNow')}
            </Link>
          </p>
        </div>
      </div>
    </AuthShell>
  );
}
